/**
 * Packages & Services builder — packages catalog section (list/cards +
 * create/edit). Extracted verbatim from the former AgencyServicesPage
 * monolith (Task 150-A). No behavior change.
 */

import { useState } from 'react';
import type { AgencyPackageRow } from '../../api/types';
import { capabilityLabelAr } from '../../permissions';
import { EmptyState } from '../states';
import { billingIntervalLabel } from './helpers';
import { isCommercialCapabilityVisible } from './launchVisibility';
import { PackageForm } from './PackageForm';

export function PackagesSection({
  packages,
  canManage,
  onSaved,
}: {
  readonly packages: ReadonlyArray<AgencyPackageRow>;
  readonly canManage: boolean;
  readonly onSaved: () => void;
}) {
  const [editing, setEditing] = useState<AgencyPackageRow | null>(null);
  const [showCreate, setShowCreate] = useState(false);

  return (
    <section className="card" style={{ padding: 22 }}>
      <div className="sec-title">
        <h2>الباقات</h2>
        <span className="meta">{packages.length}</span>
      </div>

      {packages.length === 0 ? (
        <EmptyState
          title="لا توجد باقات بعد"
          description="ابدأ بإنشاء باقة (الاسم + الوصف + السعر). ستضيف الخدمات داخلها من قسم تركيب الباقة بالأسفل."
        />
      ) : (
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: '0 0 14px',
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          {packages.map((p) => (
            <li
              key={p.id}
              style={{
                padding: '12px 14px',
                border: '1px solid var(--line)',
                borderRadius: 'var(--r-md)',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 12,
                  alignItems: 'flex-start',
                  flexWrap: 'wrap',
                }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: 14 }}>{p.name}</strong>
                    <span className={`pill pill-${p.is_active ? 'active' : 'archived'}`}>
                      {p.is_active ? 'نشطة' : 'موقوفة'}
                    </span>
                  </div>
                  {p.description ? (
                    <div className="tiny" style={{ marginTop: 4, color: 'var(--muted)' }}>
                      {p.description}
                    </div>
                  ) : null}
                  <div className="tiny" style={{ marginTop: 6, color: 'var(--muted)' }}>
                    {p.price_amount !== null ? `${p.price_amount} ${p.price_currency}` : 'بدون سعر'}{' '}
                    · {billingIntervalLabel(p.billing_interval)} · {p.services.length} خدمة
                  </div>
                  {p.services.length > 0 ? (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                      {Array.from(new Set(p.services.flatMap((s) => s.capability_keys)))
                        .filter(isCommercialCapabilityVisible)
                        .map((c) => (
                          <span key={c} className="pill pill-info" style={{ fontSize: 11 }}>
                            {capabilityLabelAr(c)}
                          </span>
                        ))}
                    </div>
                  ) : null}
                </div>
                {canManage ? (
                  <button
                    type="button"
                    className="btn-text"
                    onClick={() => setEditing(p)}
                    style={{ padding: '4px 8px', fontSize: 12 }}
                  >
                    تعديل
                  </button>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      {canManage ? (
        <button
          type="button"
          className="btn-text"
          onClick={() => setShowCreate(true)}
          style={{ padding: '8px 12px' }}
        >
          + إضافة باقة
        </button>
      ) : null}

      {editing ? (
        <PackageForm
          mode="edit"
          initial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onSaved();
          }}
        />
      ) : null}
      {showCreate ? (
        <PackageForm
          mode="create"
          initial={null}
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            onSaved();
          }}
        />
      ) : null}
    </section>
  );
}
